import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, TemplateRef, ViewChild, ViewContainerRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { RFavorietMap } from '@cumlaude/service-contract';
import { ImmediateInputComponent } from '../shared/components/immediate-input/immediate-input.component';
import { IconComponent } from '@cumlaude/shared-components-icons';

@Component({
	selector: 'app-mappen-dropdown',
	templateUrl: './mappen-dropdown.component.html',
	styleUrls: ['./mappen-dropdown.component.scss'],
	imports: [ImmediateInputComponent, IconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MappenDropdownComponent implements OnDestroy {
	private readonly changeDetector = inject(ChangeDetectorRef);
	private readonly overlay = inject(Overlay);
	private readonly overlayPositionBuilder = inject(OverlayPositionBuilder);
	private readonly viewContainerRef = inject(ViewContainerRef);

	@ViewChild('trigger', { read: ElementRef }) triggerRef!: ElementRef;
	@ViewChild('dropdown') dropdownTemplate!: TemplateRef<unknown>;

	@Input()
	mappen!: RFavorietMap[];

	@Input()
	value?: RFavorietMap;

	@Input()
	template!: RFavorietMap;

	@Output()
	valueChange = new EventEmitter<RFavorietMap>();

	@Output()
	created = new EventEmitter<RFavorietMap>();

	protected showDropdown = false;

	protected editing = false;

	private overlayRef?: OverlayRef;

	ngOnDestroy() {
		this.overlayRef?.dispose();
	}

	isEmpty() {
		return this.value === undefined;
	}

	isSelected(optie: RFavorietMap) {
		return this.value === optie;
	}

	toggleDropdown() {
		if (this.showDropdown) this.closeDropDown();
		else this.openDropdown();
	}

	select(value: RFavorietMap | undefined) {
		this.value = value;
		this.closeDropDown();
		this.valueChange.emit(value);
	}

	remove($event: Event) {
		$event.stopPropagation();
		this.select(undefined);
	}

	create(naam: string | undefined) {
		this.editing = false;
		if (naam === undefined || naam.trim() === '') return;

		const nieuweMap = { ...this.template, naam };
		this.created.emit(nieuweMap);
		this.valueChange.emit(nieuweMap);
		this.closeDropDown();
	}

	// CDK Overlay i.p.v. inline position:absolute: de lijst moet buiten een dialog met
	// overflow:hidden (afgeronde hoeken) kunnen vallen, zoals app-meatball-menu ook al doet.
	private openDropdown() {
		const positionStrategy = this.overlayPositionBuilder
			.flexibleConnectedTo(this.triggerRef)
			.withFlexibleDimensions(false)
			.withPositions([
				{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
				{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
			]);

		this.overlayRef = this.overlay.create({
			positionStrategy,
			width: this.triggerRef.nativeElement.getBoundingClientRect().width,
		});
		this.overlayRef.attach(new TemplatePortal(this.dropdownTemplate, this.viewContainerRef));
		this.overlayRef.outsidePointerEvents().subscribe(() => this.closeDropDown());

		this.showDropdown = true;
		this.changeDetector.markForCheck();
	}

	closeDropDown() {
		this.showDropdown = false;
		this.overlayRef?.dispose();
		this.overlayRef = undefined;
		this.changeDetector.markForCheck();
	}
}
